/******************************************************************************
 * Copyright (c) 2026 Contributors to the Eclipse Foundation.
 *
 * See the NOTICE file(s) distributed with this work for additional
 * information regarding copyright ownership.
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * https://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *****************************************************************************/
import { useContext } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MainContext } from '../../context';
import { useReportedQuery } from '../../hooks/use-reported-query';
import { controllerFromSignal, NO_CACHE } from '../../query-client';
const detailsKey = (name) => ['namespace-details', name];
/**
 * Loads a namespace's public details. Shared by the details form and the logo
 * sidebar, which subscribe to the same cache entry; errors surface through the
 * global error dialog.
 */
export const useNamespaceDetails = (name) => {
    const { service } = useContext(MainContext);
    return useReportedQuery(useQuery({
        queryKey: detailsKey(name),
        queryFn: ({ signal }) => service.getNamespaceDetails(controllerFromSignal(signal), name),
        ...NO_CACHE
    }));
};
/** Updates a namespace's details; on success every details consumer refreshes. */
export const useUpdateNamespaceDetails = () => {
    const { service } = useContext(MainContext);
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ detailsUrl, details }) => service.setNamespaceDetails(new AbortController(), detailsUrl, details),
        onSuccess: (_result, { details }) => {
            queryClient.invalidateQueries({ queryKey: detailsKey(details.name) });
        }
    });
};
/** Uploads a namespace logo; on success every details consumer refreshes. */
export const useUpdateNamespaceLogo = () => {
    const { service } = useContext(MainContext);
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ detailsUrl, file, fileName }) => service.setNamespaceLogo(new AbortController(), detailsUrl, file, fileName),
        onSuccess: (_result, { namespaceName }) => {
            queryClient.invalidateQueries({ queryKey: detailsKey(namespaceName) });
        }
    });
};
//# sourceMappingURL=use-namespace-details.js.map